import {
  FieldMappingBaseFieldType,
  FieldMappingObjectType,
  FieldMappingType,
} from '@monetize/types/app';

/** The CRM objects each connection maps, one field-mapping section apiece. */
export const CRM_FIELD_MAPPING_OBJECT_TYPES = {
  hubspot: ['companies', 'contacts', 'deals', 'products'],
  salesforce: ['Account', 'Contact', 'Opportunity', 'Product2'],
} as const satisfies Record<string, readonly FieldMappingObjectType[]>;

export const MN_REFERENCE_TYPE_FIELD_API_NAMES = new Set([
  'legalEntityId.customId',
  'legalEntityId.name',
  'contacts.esign',
]);
export const MN_QUOTE_DEPENDENT_FIELD_API_NAMES = new Set([
  'legalEntityId.customId',
  'legalEntityId.name',
  'currency',
  'contacts.esign',
]);
export const MN_BLACKLISTED_FIELD_API_NAMES = new Set(['legalEntityId']);
export const HS_VALIDATION_IGNORABLE_FIELD_API_NAMES = new Set([
  'hs_object_id',
]);

export const MN_CRM_FIELDS = {
  Quote: [
    {
      fieldApiName: 'description',
      dataType: 'text',
      displayLabel: 'Description',
    },
    {
      fieldApiName: 'customId',
      dataType: 'text',
      displayLabel: 'Custom ID',
    },
    {
      fieldApiName: 'currency',
      dataType: 'text',
      displayLabel: 'Quote Currency',
    },
    {
      fieldApiName: 'legalEntityId.customId',
      dataType: 'text',
      displayLabel: 'Legal Entity Custom ID',
    },
    {
      fieldApiName: 'legalEntityId.name',
      dataType: 'text',
      displayLabel: 'Legal Entity Name',
    },
  ],
  Account: [
    {
      fieldApiName: 'ccEmails',
      dataType: 'text',
      displayLabel: 'Account Level CC Emails',
    },
    {
      fieldApiName: 'defaultCurrency',
      dataType: 'text',
      displayLabel: 'Currency',
    },
    {
      fieldApiName: 'defaultDunningProcessId',
      dataType: 'text',
      displayLabel: 'Default Dunning Process ID',
    },
    {
      fieldApiName: 'defaultLegalEntityId',
      dataType: 'text',
      displayLabel: 'Legal Entity ID',
    },
    {
      fieldApiName: 'customId',
      dataType: 'text',
      displayLabel: 'Custom ID',
    },
  ],
  Contact: [],
  companies: [],
  deals: [],
  contacts: [],
  LegalEntity: [],
  Opportunity: [],
  CreditNote: [],
  CreditMemo: [],
  Invoice: [],
  Product: [
    {
      fieldApiName: 'name',
      dataType: 'text',
      displayLabel: 'Name',
    },
    {
      fieldApiName: 'description',
      dataType: 'text',
      displayLabel: 'Description',
    },
    {
      fieldApiName: 'customId',
      dataType: 'text',
      displayLabel: 'Custom ID',
    },
    {
      fieldApiName: 'financeId',
      dataType: 'text',
      displayLabel: 'Finance ID',
    },
    {
      fieldApiName: 'sku',
      dataType: 'text',
      displayLabel: 'SKU',
    },
    {
      fieldApiName: 'status',
      dataType: 'text',
      displayLabel: 'Status',
    },
    {
      fieldApiName: 'productType',
      dataType: 'text',
      displayLabel: 'Product Type',
    },
    {
      fieldApiName: 'prorationPolicy',
      dataType: 'text',
      displayLabel: 'Proration Policy',
    },
    {
      fieldApiName: 'recognitionMethod',
      dataType: 'text',
      displayLabel: 'Recognition Method',
    },
    {
      fieldApiName: 'recognitionDuration',
      dataType: 'number',
      displayLabel: 'Recognition Duration',
    },
    {
      fieldApiName: 'ppuDisplayPrecision',
      dataType: 'number',
      displayLabel: 'PPU Display Precision',
    },
    {
      fieldApiName: 'taxItemCode',
      dataType: 'text',
      displayLabel: 'Tax Item Code',
    },
    {
      fieldApiName: 'taxExempt',
      dataType: 'boolean',
      displayLabel: 'Tax Exempt',
    },
  ],
  Offering: [
    {
      fieldApiName: 'name',
      dataType: 'text',
      displayLabel: 'Name',
    },
    {
      fieldApiName: 'description',
      dataType: 'text',
      displayLabel: 'Description',
    },
    {
      fieldApiName: 'customId',
      dataType: 'text',
      displayLabel: 'Custom ID',
    },
    {
      fieldApiName: 'financeId',
      dataType: 'text',
      displayLabel: 'Finance ID',
    },
    {
      fieldApiName: 'status',
      dataType: 'text',
      displayLabel: 'Status',
    },
    {
      fieldApiName: 'type',
      dataType: 'text',
      displayLabel: 'Type',
    },
    {
      fieldApiName: 'startDate',
      dataType: 'date',
      displayLabel: 'Start Date',
    },
    {
      fieldApiName: 'endDate',
      dataType: 'date',
      displayLabel: 'End Date',
    },
  ],
  // Salesforce-side object; MonetizeNow has no fields of its own here.
  Product2: [],
  // HubSpot's Product object; a mapping target only, so it lists no MonetizeNow
  // fields. The MonetizeNow Product fields mapped to it are under `Product`.
  products: [],
  system: 'monetizenow',
} satisfies FieldMappingType;

export const MN_CRM_FIELDS_FOR_SALESFORCE = {
  ...MN_CRM_FIELDS,
  Quote: [
    ...(MN_CRM_FIELDS.Quote ?? []),
    {
      fieldApiName: 'contacts.esign',
      dataType: 'text',
      displayLabel: 'Contact eSign Email',
    },
    {
      fieldApiName: 'linkedAccountId',
      dataType: 'text',
      displayLabel: 'Linked Account ID',
    },
  ],
} satisfies FieldMappingType;

/**
 * Salesforce Account fields hidden from the *outbound* (MonetizeNow ->
 * Salesforce) Account target dropdown. We populate these fields manually on
 * update, so they must not be offered as user-defined mapping targets.
 * Matched exactly against the Salesforce field API name. The inbound
 * Salesforce -> MonetizeNow list is unaffected.
 */
export const SF_ACCOUNT_OUTBOUND_EXCLUDED_FIELD_API_NAMES = new Set([
  'Name',
  'BillingCity',
  'BillingCountry',
  'BillingPostalCode',
  'BillingState',
  'BillingStreet',
  'ShippingCity',
  'ShippingCountry',
  'ShippingPostalCode',
  'ShippingState',
  'ShippingStreet',
]);

/**
 * MonetizeNow Account fields that are only offered when mapping *out* to
 * Salesforce (Account -> Account, fromMn). Kept separate from
 * `MN_CRM_FIELDS.Account` because that list is the target of the inbound
 * Salesforce -> MonetizeNow mapping (and is spread into the HubSpot config);
 * these are read-only rollups that only make sense in the outbound direction.
 */
export const MN_ACCOUNT_OUTBOUND_FIELDS_FOR_SALESFORCE = [
  {
    fieldApiName: 'amountOutstanding',
    dataType: 'number',
    displayLabel: 'Amount Outstanding',
  },
  {
    fieldApiName: 'pastDueAmountOutstanding',
    dataType: 'number',
    displayLabel: 'Past Due Amount Outstanding',
  },
  {
    fieldApiName: 'pastDueInvoiceCount',
    dataType: 'number',
    displayLabel: 'Past Due Invoice Count',
  },
  {
    fieldApiName: 'minimumInvoiceDueDate',
    dataType: 'date',
    displayLabel: 'Minimum Invoice Due Date',
  },
] satisfies FieldMappingBaseFieldType[];

export const MN_CRM_FIELDS_FOR_HUBSPOT = {
  ...MN_CRM_FIELDS,
  Contact: [
    ...(MN_CRM_FIELDS.Contact ?? []),
    {
      fieldApiName: 'customId',
      dataType: 'text',
      displayLabel: 'Custom ID',
    },
  ],
} satisfies FieldMappingType;
